import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { HeroModern } from "../components/HeroModern";
import { AnimatedStats } from "../components/AnimatedStats";
import { Testimonials } from "../components/Testimonials";
import { TrustedBy } from "../components/TrustedBy";
import { suites } from "../data/suites";
import { industries } from "../data/industries";

/**
 * Home page — adapts to session state:
 * - Authenticated: short hero + CTA to catalog
 * - Unauthenticated (Cycle 8 STORY-701): full landing experience
 *   - Modern animated hero (gradient mesh + dual CTA)
 *   - Animated stats counter
 *   - Suite preview cards (3 suites)
 *   - Testimonials section
 */
export function HomePage(): ReactElement {
  const { session } = useAuth();

  if (session?.authenticated) {
    return (
      <section className="home-hero">
        <h1 className="home-hero__title">
          Здравствуйте{session.client_name ? `, ${session.client_name}` : ""}!
        </h1>
        <p className="home-hero__subtitle">
          Перейдите в каталог — мы подобрали продукты специально для вас.
        </p>
        <Link to="/catalog" className="btn-primary home-hero__cta">
          Открыть каталог
        </Link>
      </section>
    );
  }

  return (
    <>
      <HeroModern />
      <AnimatedStats />

      {/* Cycle 9 STORY-804: Trust signals (placeholder) */}
      <TrustedBy />

      {/* Cycle 8 STORY-702: Suite preview cards on Home */}
      <section className="suite-preview" aria-labelledby="suite-preview-title">
        <header className="suite-preview__header">
          <h2 id="suite-preview-title" className="suite-preview__title">
            Решения по направлениям
          </h2>
          <p className="suite-preview__subtitle">
            Продукты сгруппированы в 3 коммерческих суита для разных бизнес-задач
          </p>
        </header>
        <div className="suite-preview__grid">
          {suites.map((suite) => (
            <Link
              key={suite.slug}
              to={`/suite/${suite.slug}`}
              className="suite-preview-card"
              style={
                {
                  "--suite-accent": suite.accentColor,
                } as React.CSSProperties
              }
            >
              <span
                className="suite-preview-card__pill"
                style={{ backgroundColor: suite.accentColor }}
              >
                {suite.label}
              </span>
              <h3 className="suite-preview-card__title">{suite.heroTitle}</h3>
              <p className="suite-preview-card__subtitle">{suite.heroSubtitle}</p>
              <div className="suite-preview-card__cta">
                Узнать больше →
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Cycle 9 STORY-803: Industry preview cards */}
      <section className="industry-preview" aria-labelledby="industry-preview-title">
        <header className="suite-preview__header">
          <h2 id="industry-preview-title" className="suite-preview__title">
            Решения по отраслям
          </h2>
          <p className="suite-preview__subtitle">
            Перейдите к решениям, релевантным вашей индустрии
          </p>
        </header>
        <div className="industry-preview__grid">
          {industries.slice(0, 5).map((ind) => (
            <Link
              key={ind.slug}
              to={`/industry/${ind.slug}`}
              className="industry-preview-card"
              style={{ "--industry-accent": ind.accentColor } as React.CSSProperties}
            >
              <span className="industry-preview-card__label">{ind.label}</span>
              <h3 className="industry-preview-card__title">{ind.heroTitle}</h3>
              <span className="industry-preview-card__cta">Узнать больше →</span>
            </Link>
          ))}
        </div>
      </section>

      <Testimonials />

      {/* Bottom CTA */}
      <section className="home-bottom-cta">
        <h2>Готовы обсудить применение IT-Navigate в вашей компании?</h2>
        <p>
          Бесплатная демонстрация под ваши задачи — без обязательств. Менеджер
          свяжется с вами в течение 1 рабочего дня.
        </p>
        <Link to="/request-demo" className="btn-primary home-bottom-cta__btn">
          Запросить демонстрацию →
        </Link>
      </section>
    </>
  );
}
